import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { api } from '@/lib/api/client';
import { toast } from 'sonner';
import { AdminLayout } from '@/components/layouts/admin-layout';
import FilterBar from '@/components/common/filter-bar';
import { StreamRecordingDetails } from '@/components/admin/stream-recording-details';
import { VirtualizedGrid } from '@/components/common/virtualized-grid';
import { RecordingCard } from '@/components/admin/recording-card';
import { RECORDING_FILTERS } from '@/lib/shared-types/filters';

interface Recording {
  id: string;
  streamId: number;
  streamTitle: string;
  title: string;
  duration: number;
  fileSize: number;
  createdAt: string;
  status: 'processing' | 'ready' | 'failed';
  downloadUrl?: string;
  thumbnailUrl?: string | null;
}

interface RecordingsResponse {
  recordings: Recording[];
  total: number;
}

export default function AdminRecordingsPage() {
  const [recordings, setRecordings] = useState<Recording[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedRecording, setSelectedRecording] = useState<Recording | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const navigate = useNavigate();

  // Filter state managed by FilterBar
  const [filters, setFilters] = useState<Record<string, string | string[]>>({});

  useEffect(() => {
    setPage(1);
    setHasMore(false);
  }, [filters]);

  const fetchRecordings = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        pageSize: '20',
        ...filters
      });

      const response = await api.get<RecordingsResponse>(`/api/recordings?${params.toString()}`);
      if (response.success && response.data) {
        const data = response.data;
        setRecordings(prev => {
          if (page === 1) {
            return data.recordings || [];
          }
          return [...prev, ...(data.recordings || [])];
        });
        const fetchedCount = (data.recordings || []).length;
        setHasMore(fetchedCount === 20 && data.total > page * 20);
      } else {
        const errMsg = response.error?.message || 'Failed to load recordings';
        setError(errMsg);
        toast.error(errMsg);
      }
    } catch (err) {
      console.error('Failed to fetch recordings:', err);
      setError('Failed to load recordings');
      setRecordings([]);
    } finally {
      setIsLoading(false);
    }
  }, [page, filters]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  const loadMore = () => {
    if (hasMore && !isLoading) {
      setPage(p => p + 1);
    }
  };

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return '0 B';
    const k = 1024;
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const formatDuration = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    const minutes = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    if (hours > 0) {
      return `${hours}:${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
    }
    return `${minutes}:${secs.toString().padStart(2, '0')}`;
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ready':
        return <Badge variant="default">Ready</Badge>;
      case 'processing':
        return <Badge variant="secondary">Processing</Badge>;
      case 'failed':
        return <Badge variant="destructive">Failed</Badge>;
      default:
        return <Badge variant="outline">{status}</Badge>;
    }
  };

  const filteredRecordings = recordings.filter(rec => {
    const search = (filters.search as string) || '';
    const status = (filters.status as string) || '';

    const matchesSearch = search === '' ||
      rec.title.toLowerCase().includes(search.toLowerCase()) ||
      rec.streamTitle?.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = status === '' || status === 'all' || rec.status === status;
    return matchesSearch && matchesStatus;
  });

  return (
    <AdminLayout>
      {/* Filters */}
      <div className="p-2 sm:p-3 @[740px]/admin-layout:p-4">
        <FilterBar
          filters={RECORDING_FILTERS}
          activeFilters={filters}
          onFilterChange={setFilters}
        />
      </div>

      {/* Recordings Grid */}
      <section className="space-y-3">
        <div className="px-4 text-sm text-muted-foreground sm:px-6">
          {filteredRecordings.length} recordings
        </div>
        <div className="h-[600px] px-4 sm:px-6">
          <VirtualizedGrid
            items={filteredRecordings}
            renderItem={(recording) => (
              <RecordingCard
                recording={recording}
                onClick={() => {
                  setSelectedRecording(recording);
                  setDetailsDialogOpen(true);
                }}
              />
            )}
            keyExtractor={(recording) => recording.id}
            columns={{ sm: 1, md: 1, lg: 2, xl: 2 }}
            gap="gap-4"
            loading={isLoading}
            hasNextPage={hasMore}
            onLoadNextPage={loadMore}
            loadMoreThreshold={5}
            viewMode="list"
            itemHeight={100}
            emptyMessage="No recordings found"
          />
        </div>
      </section>

      {/* Recording Details Dialog */}
      <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Recording Details</DialogTitle>
            <DialogDescription>
              View details and download recording
            </DialogDescription>
          </DialogHeader>
          {selectedRecording && (
            <StreamRecordingDetails 
              streamId={selectedRecording.streamId} 
              recordingId={selectedRecording.id} 
            />
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
